import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { select, Store } from '@ngrx/store';

import { isEqual } from 'lodash-es';
import { BehaviorSubject, debounceTime, Observable, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { FORM_SECTION } from '../../../../../constants/form.constant';
import { NetSheetPartyLabelEnum, NetSheetPartyTypeEnum } from '../../../../../enums/net-sheet';
import * as fromNetSheetActions from '../../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../../store/selectors/net-sheet.selectors';
import { FormSection } from '../../../../../typings/form';
import { NetSheetFormFieldSettings } from '../../../../../typings/net-sheet';
import { GenericComponent } from '../../../../components/generic/generic.component';

interface FormModel {
  customerInformationPartyFirstName: FormControl<string | null>;
  customerInformationPartyFullName: FormControl<string | null>;
  customerInformationPartyLastName: FormControl<string | null>;
  customerInformationPartyType: FormControl<NetSheetPartyTypeEnum | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FORM_SECTION,
      useExisting: CustomerInformationComponent
    }
  ],
  selector: 'red-net-sheet-customer-information',
  standalone: false,
  templateUrl: './customer-information.component.html'
})
export class CustomerInformationComponent extends GenericComponent implements OnInit, FormSection {
  private store = inject(Store);

  form = new FormGroup<FormModel>({
    customerInformationPartyFirstName: new FormControl<string | null>(null),
    customerInformationPartyFullName: new FormControl<string | null>(null),
    customerInformationPartyLastName: new FormControl<string | null>(null),
    customerInformationPartyType: new FormControl<NetSheetPartyTypeEnum | null>(NetSheetPartyTypeEnum.Seller)
  });

  customerInformationPartyTypes$: Observable<NetSheetPartyTypeEnum[]>;

  fieldsSettings$ = new BehaviorSubject<{ [key: string]: NetSheetFormFieldSettings } | null>(null);

  constructor() {
    super();

    this.form.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged((previous, current) => isEqual(previous, current))
      )
      .subscribe((values) => {
        this.store.dispatch(
          fromNetSheetActions.updateCustomerInformation({
            payload: values
          })
        );
        this.valid.set(this.form.valid);
      });

    this.customerInformationPartyTypes$ = this.store.pipe(
      select(fromNetSheetSelectors.selectCustomerInformationPartyTypes),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );
  }

  ngOnInit() {
    this.addUniqueSubscription(
      'fieldsDataSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectCustomerInformationFieldsData),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((data) => {
          this.form.patchValue(data, { emitEvent: false });
          this.valid.set(this.form.valid);
        })
    );

    this.addUniqueSubscription(
      'fieldsSettingsSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectCustomerInformationFieldsSettings),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((fields) => {
          this.fieldsSettings$.next(fields);
          setTimeout(() => {
            this.valid.set(this.form.valid);
          });
        })
    );
  }

  protected readonly customerTypes = NetSheetPartyTypeEnum;
  protected readonly netSheetCustomerTypeLabelEnum = NetSheetPartyLabelEnum;
  readonly valid = signal(this.form.valid);
}
